import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Wrap any /hotel/* page that requires the Hotel Core capability.
 * - Platform users (no tenant) are redirected to /tenants
 * - Tenants without hotel_core in enabled_modules see a friendly locked notice.
 * Backend already enforces `require_module("hotel_core")` on every /api/hotel/*
 * endpoint, so this is purely UX: prevents an empty shell with toast errors
 * when a non-entitled user deep-links to a hotel URL.
 */
export default function HotelModuleGuard({ children }) {
  const { user, tenant, checking } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!checking && user && !tenant) {
      navigate("/tenants", { replace: true });
    }
  }, [checking, user, tenant, navigate]);

  if (checking) {
    return (
      <div className="surface-card p-12 text-center text-[#475569]" data-testid="hmg-loading">
        Caricamento…
      </div>
    );
  }

  const enabled = tenant?.enabled_modules || [];
  const hasAccess = enabled.includes("hotel_core");

  if (!hasAccess) {
    return (
      <div className="surface-card p-12 text-center" data-testid="hmg-blocked">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#FFF7E6] grid place-items-center mb-4">
          <Lock className="w-6 h-6 text-[#A36B0F]" />
        </div>
        <h2 className="font-heading text-xl font-bold text-[#0f172a]">Modulo Hotel non incluso</h2>
        <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">
          Il tuo piano attuale non include il modulo Hotel Starter. Aggiorna a un piano
          Hotel o Combo Premium per accedere alle funzionalità di gestione struttura.
        </p>
        <a
          href="/billing"
          className="inline-flex mt-4 px-4 py-2 text-sm font-semibold rounded-md bg-[#102A43] hover:bg-[#0B1B2C] text-white"
          data-testid="hmg-upgrade-btn"
        >
          Vai a Fatturazione
        </a>
      </div>
    );
  }

  return children;
}
